import dayjs from 'dayjs';
import { formatYearDate, safeParseDateInput } from '../../../helpers/date-time-helpers';
import type { WorkPeriod } from '../../work-periods/work-periods.type';

/**
 * Filter work periods by selected year
 *
 * @param workPeriods - The work periods
 * @param selectedYear - The selected year
 * @returns The filtered work periods
 */
function filterWorkPeriodsByYear(workPeriods: WorkPeriod[] | undefined, selectedYear: number | null): WorkPeriod[] {
  if (!selectedYear || !workPeriods) return [];

  return workPeriods.filter((workPeriod) => {
    return (
      formatYearDate(workPeriod.end_date) === selectedYear.toString() &&
      safeParseDateInput(workPeriod.end_date)?.isBefore(dayjs()) === true
    );
  });
}

/**
 * Get the total spending for a given year
 *
 * @param workPeriods - The work periods
 * @param selectedYear - The selected year
 * @returns The total spending for the given year
 */
function getTotalSpending(workPeriods: WorkPeriod[] | undefined, selectedYear: number | null): number | null {
  const filteredWorkPeriods = filterWorkPeriodsByYear(workPeriods, selectedYear);
  if (filteredWorkPeriods.length === 0) {
    return null;
  }
  return filteredWorkPeriods.reduce((acc, workPeriod) => acc + (workPeriod.budget || 0), 0);
}

/**
 * Get the average spending for a given year
 *
 * @param workPeriods - The work periods
 * @param selectedYear - The selected year
 * @returns The average spending for the given year
 */
function getAverageSpendingPerSprint(
  workPeriods: WorkPeriod[] | undefined,
  selectedYear: number | null,
): number | null {
  const filteredWorkPeriods = filterWorkPeriodsByYear(workPeriods, selectedYear);
  if (filteredWorkPeriods.length === 0) {
    return null;
  }
  return (
    filteredWorkPeriods.reduce((acc, workPeriod) => acc + (workPeriod.budget || 0), 0) / filteredWorkPeriods.length
  );
}

/**
 * Get the median spending for a given year
 *
 * @param workPeriods - The work periods
 * @param selectedYear - The selected year
 * @returns The median spending for the given year
 */
function getMedianSpendingPerSprint(workPeriods: WorkPeriod[] | undefined, selectedYear: number | null): number | null {
  const filteredWorkPeriods = filterWorkPeriodsByYear(workPeriods, selectedYear);
  if (filteredWorkPeriods.length === 0) {
    return null;
  }
  const sortedBudgets = filteredWorkPeriods.map((workPeriod) => workPeriod.budget ?? 0).sort((a, b) => a - b);
  const midpoint = Math.floor(sortedBudgets.length / 2);
  return sortedBudgets.length % 2 === 0
    ? (sortedBudgets[midpoint - 1] + sortedBudgets[midpoint]) / 2
    : sortedBudgets[midpoint];
}

/**
 * Get the minimum spending for a given year
 *
 * @param workPeriods - The work periods
 * @param selectedYear - The selected year
 * @returns The minimum spending for the given year
 */
function getMinimumSpendingPerSprint(
  workPeriods: WorkPeriod[] | undefined,
  selectedYear: number | null,
): number | null {
  const filteredWorkPeriods = filterWorkPeriodsByYear(workPeriods, selectedYear);
  if (filteredWorkPeriods.length === 0) {
    return null;
  }
  return Math.min(...filteredWorkPeriods.map((workPeriod) => workPeriod.budget || 0));
}

/**
 * Get the maximum spending for a given year
 *
 * @param workPeriods - The work periods
 * @param selectedYear - The selected year
 * @returns The maximum spending for the given year
 */
function getMaximumSpendingPerSprint(
  workPeriods: WorkPeriod[] | undefined,
  selectedYear: number | null,
): number | null {
  const filteredWorkPeriods = filterWorkPeriodsByYear(workPeriods, selectedYear);
  if (filteredWorkPeriods.length === 0) {
    return null;
  }
  return Math.max(...filteredWorkPeriods.map((workPeriod) => workPeriod.budget || 0));
}

export {
  getAverageSpendingPerSprint,
  getMaximumSpendingPerSprint,
  getMedianSpendingPerSprint,
  getMinimumSpendingPerSprint,
  getTotalSpending,
};
